import { createAction, createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';
import { createAsyncAction, applyParamValues, setParamValue } from '@smorder/logic';


const INITIAL_STATE = Immutable({
  selectedLocation: null,
  selectedReport: null,
  filters: [],
  locations: [],
  reports: [],
  reportData: [],
  loadingReports: [],
  params: [],
  pdfSupportsCsvExport: false,
  isLoadingLocations: false,
  isLoadingReports: false,
  isLoadingCsvReportData: false,
});

export const selectLocation = createAction('SELECT_LOCATION');
export const selectReport = createAction('SELECT_REPORT');
export const selectReportByWidgetId = createAction('SELECT_REPORT_BY_WIDGET');

export const loadLocationsAction = createAsyncAction('loadLocations');

export const loadReportData = createAction('LOAD_REPORT_DATA');
export const loadReportDataSucceeded = createAction('LOAD_REPORT_DATA_SUCCEEDED');
export const loadReportDataFailed = createAction('LOAD_REPORT_DATA_FAILED');
export const removeReportData = createAction('REMOVE_REPORT_DATA');

export const selectLocationOnLoad = createAction('SELECT_LOCATION_ON_LOAD');
export const selectReportOnLoad = createAction('SELECT_REPORT_ON_LOAD');

export const loadAllReportData = createAction('LOAD_ALL_REPORT_DATA');

export const loadParamsAction = createAsyncAction('loadParams');
export const setParamValueAction = createAction('setParamValue');

export const loadCsvReportDataAction = createAsyncAction('loadCsvReportData');


const reducers = createReducer({
  [selectLocation]: (state, locationId) => {
    const location = state.locations.find(l => l.id === locationId) || state.locations[0];

    if (!location) {
      return state;
    }

    if (location && location.name) {
      document.title = `Reports - ${location.name}`;
    }

    return state.merge({
      selectedLocation: location,
    });
  },
  [selectReport]: (state, reportId) => {
    if (!state.selectedLocation) {
      return state;
    }

    const report = state.reports.find(r => r.reportID === reportId)
      || state.reports.find(r => r.reportID === state.selectedLocation.reportAssignments[0].reportId);

    const filters = report && report.filters ? JSON.parse(report.filters) : [];

    return state.merge({
      selectedReport: report,
      filters,
      params: [],
    });
  },
  [loadLocationsAction.INIT]: state => state.merge({
    locations: state.locations || [],
    reportData: [],
    isLoadingLocations: true,
    isLoadingReports: true,
  }),
  [loadLocationsAction.FAILED]: state => state.merge({ isLoadingLocations: false, isLoadingReports: false }),
  [loadLocationsAction.DONE]: (state, { locations, reports }) => { 
    const locationIds = {};
    locations?.forEach(location => locationIds[location.id] = location);

    return state.merge({
      locations,
      locationIds,
      reports,
      selectedLocation: state.selectedLocation || (locations ? locations[0] : null),
      isLoadingLocations: false,
      isLoadingReports: false,
    });
  },
  [loadReportData]: (state, payload) => {
    const { reportDataId, widgetId } = payload;
    const loadingReports = state.loadingReports
      .filter(r => r.widgetId !== widgetId)
      .concat({ reportDataId, widgetId });

    return state.merge({ loadingReports });
  },
  [loadReportDataSucceeded]: (state, {
    reportDataId, reportData, locationId, widgetId,
  }) => {
    const oldReportData = state.reportData.filter(d => d.reportDataId !== reportDataId
      || d.widgetId !== widgetId
      || d.locationId !== locationId);

    const newLoadingReports = state.loadingReports.filter(r => r.reportDataId !== reportDataId || r.widgetId !== widgetId);

    const newReportData = ([{
      reportDataId,
      locationId,
      widgetId,
      reportData: {
        ...reportData,
        reportDataId,
        comparison: reportDataId !== 0,
      },
    }].concat(oldReportData)).sort((a, b) => a.reportDataId - b.reportDataId);

    return state.merge({ reportData: newReportData, loadingReports: newLoadingReports });
  },
  [loadReportDataFailed]: (state, widgetId) => {
    const oldLoadingReports = state.loadingReports;
    const newLoadingReports = oldLoadingReports.filter(r => r.widgetId !== widgetId);
    return state.merge({ loadingReports: newLoadingReports });
  },
  [removeReportData]: (state, reportDataId) => {
    const newReportData = state.reportData.filter(d => d.reportDataId !== reportDataId);

    return state.merge({ reportData: newReportData });
  },
  [loadParamsAction.DONE]: (state, newParams) => {
    const { data, supportsCsvExport } = newParams;

    if (!data) {
      return state.merge({ params: INITIAL_STATE.params, supportsCsvExport: INITIAL_STATE.pdfSupportsCsvExport });
    }

    const params = applyParamValues(state.params, data);

    return state.merge({ params, pdfSupportsCsvExport: supportsCsvExport });
  },
  [setParamValueAction]: (state, { name, value }) => {
    const params = setParamValue(state.params, name, value);

    return state.merge({ params });
  },
  [selectLocationOnLoad]: (state, locationIdOnLoad) => state.merge({ locationIdOnLoad }),
  [loadCsvReportDataAction.INIT]: state => state.merge({ isLoadingCsvReportData: true }),
  [loadCsvReportDataAction.DONE]: state => state.merge({ isLoadingCsvReportData: false }),
  [loadCsvReportDataAction.FAILED]: state => state.merge({ isLoadingCsvReportData: false }),
}, INITIAL_STATE);

export default reducers;
